// ---------------------------------------------------------------------------
// ProgressDrawerV3 — estilos especificos do drawer "Progresso da modelagem".
// ---------------------------------------------------------------------------
// Reusa o chassi/rail/header do ``inputs_v3_components/drawer/styles.js``
// (overlay, drawer, drawerHeader, drawerBody, rail, content, etc.) e
// adiciona apenas o que esse drawer tem de proprio:
//
//   - "Cockpit strip"  → faixa fixa entre header e (rail+conteudo) com
//                         4 KPIs e barra de progresso multi-segmento.
//                         Por decisao do usuario (sub-leva A), essa
//                         faixa fica sempre visivel ao trocar de pane.
//   - Action item       → cartao de "Pendencias" (decisao / upload /
//                         erro / recalc), portado do
//                         ``ProgressDashboardModal``.
//   - Event item        → linha da timeline da implementacao, similar
//                         ao tlItem do drawer do input mas em escopo
//                         de mes/implementacao.
//   - Blocks list       → lista de blocos de calculo, cada um com
//                         pill de status, duracao, click para abrir
//                         o log do bloco.
// ---------------------------------------------------------------------------

import { makeStyles } from "@material-ui/core/styles";
import {
  grayColor,
  whiteColor,
  successColor,
  warningColor,
  dangerColor,
  blendColors,
} from "../../../../assets/jss/material-dashboard-pro-react";

// Mesma largura do drawer do input (1000px). Mantem consistencia visual
// entre os dois "drawers gemeos" da V3.
export { DRAWER_WIDTH } from "../drawer/styles";

// Cores semanticas — espelham as do ProgressDashboardModal.js (origem
// do design). A migracao para drawer mantem a mesma paleta.
export const PROGRESS_COLORS = {
  done: "#2e7d32",
  stale: "#8bc34a",
  action: "#c62828",
  waiting: "#c7cbd1",
  decision: "#ef6c00",
  info: "#1565c0",
};

// Helper local: fundo "soft" de uma cor (mesma logica do modal antigo).
// blendColors(A, B, alpha) = peso A * alpha + peso B * (1 - alpha).
// Para fundo claro: blendColors(COLOR, "#fff", 0.15) = 15% color + 85% white.
export const softBg = (color) => blendColors(color, whiteColor, 0.15);

const useProgressStyles = makeStyles((theme) => ({
  // ----- Header extras (mes selector + chevrons ‹/›) ---------------------
  // Grupo da pilula + chevrons. Mantemos compacto (sem gap excessivo)
  // para nao competir com o titulo do header.
  monthSelectorGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  monthChevron: {
    background: "transparent",
    border: "none",
    color: whiteColor,
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
    transition: "background 150ms ease, opacity 150ms ease",
    "& .MuiSvgIcon-root": { fontSize: 18 },
    "&:hover:not(:disabled)": { background: "rgba(255,255,255,0.12)" },
    "&:disabled": {
      opacity: 0.3,
      cursor: "not-allowed",
    },
  },
  monthSelectorBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 11px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.12)",
    color: whiteColor,
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 150ms ease",
    "&:hover": { background: "rgba(255,255,255,0.18)" },
    "& .MuiSvgIcon-root": { fontSize: 16, marginRight: -4 },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  monthSelectorMenu: {
    "& .MuiPaper-root": {
      minWidth: 180,
      maxHeight: 360,
      borderRadius: 6,
    },
  },
  monthMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    fontSize: 13,
    minHeight: 0,
    "& .month-status-dot": {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
  },

  // ----- Cockpit strip (sempre visivel) ----------------------------------
  cockpit: {
    flexShrink: 0,
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[5]}`,
    padding: "10px 16px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
  },
  kpi: {
    border: `1px solid ${grayColor[8]}`,
    borderRadius: 6,
    padding: "6px 12px 8px",
    background: whiteColor,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  kpiLabel: {
    fontSize: 9.5,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: grayColor[1],
  },
  kpiValue: {
    fontSize: 18,
    fontWeight: 400,
    color: grayColor[2],
    letterSpacing: "-0.01em",
    lineHeight: 1.15,
  },
  kpiValueTotal: {
    color: grayColor[0],
    fontSize: 12,
    fontWeight: 300,
    marginLeft: 2,
  },
  kpiHint: {
    fontSize: 10,
    color: grayColor[1],
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    marginTop: 1,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "1px 6px",
    borderRadius: 999,
    fontSize: 9.5,
  },
  chipDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },
  // Progress row dentro do cockpit (mais compacto que no modal antigo).
  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  progressLabel: {
    fontSize: 10,
    color: grayColor[1],
    minWidth: 84,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  progressBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    background: grayColor[12],
    overflow: "hidden",
    display: "flex",
  },
  progressLegend: {
    fontSize: 10,
    color: grayColor[1],
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },

  // ----- Action item (Pendencias pane) -----------------------------------
  // Cartao individual da lista de pendencias. 4 colunas: icone, corpo
  // (titulo + meta), responsavel, idade. Hover sutil.
  actionItem: {
    padding: "11px 18px",
    display: "grid",
    gridTemplateColumns: "28px 1fr 150px 70px",
    alignItems: "center",
    gap: 12,
    borderBottom: `1px solid ${grayColor[12]}`,
    cursor: "pointer",
    transition: "background 120ms",
    "&:hover": { background: grayColor[13] },
    "&:last-child": { borderBottom: "none" },
  },
  actionIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 500,
  },
  actionBody: { minWidth: 0 },
  actionTitle: { fontSize: 13, color: grayColor[2], fontWeight: 400 },
  actionMeta: {
    fontSize: 11,
    color: grayColor[1],
    marginTop: 2,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  actionPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 6px",
    borderRadius: 999,
    background: grayColor[12],
    fontSize: 10,
  },
  actionResp: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: grayColor[1],
    minWidth: 0,
  },
  actionRespAvatar: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9.5,
    fontWeight: 500,
  },
  actionRespName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: grayColor[2],
    fontWeight: 400,
  },
  actionAge: {
    fontSize: 11,
    color: grayColor[1],
    whiteSpace: "nowrap",
    textAlign: "right",
  },

  // ----- Pendencias matriz (ActionsPane — variante D) --------------------
  pendMapBody: {
    padding: "0 18px",
  },
  pendMatrixWrap: {
    overflowX: "auto",
    marginTop: 14,
    paddingBottom: 14,
    borderBottom: `1px solid ${grayColor[12]}`,
  },
  pendMatrix: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    minWidth: 480,
  },
  pendMatrixTh: {
    padding: "10px 12px",
    textAlign: "center",
    border: `1px solid ${grayColor[12]}`,
    background: grayColor[13],
    fontWeight: 600,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: grayColor[1],
  },
  pendMatrixThPerson: {
    textAlign: "left",
    minWidth: 140,
  },
  pendMatrixTd: {
    padding: "10px 12px",
    textAlign: "center",
    border: `1px solid ${grayColor[12]}`,
  },
  pendMatrixPerson: {
    textAlign: "left",
    fontWeight: 500,
    color: grayColor[2],
    background: whiteColor,
  },
  pendCell: {
    minWidth: 72,
    color: grayColor[0],
    background: grayColor[13],
    fontSize: 13,
  },
  pendCellActive: {
    background: "#fff8f8",
    color: "#c62828",
    fontWeight: 700,
    fontSize: 16,
    cursor: "pointer",
    "&:hover": { background: "#ffebee" },
  },
  pendCellSelected: {
    outline: `2px solid ${blendColors("#c62828", whiteColor, 0.35)}`,
    outlineOffset: -2,
  },
  pendDetailsRoot: {
    padding: "0 0 18px",
  },
  pendDetail: {
    padding: "14px 18px",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[12]}`,
    scrollMarginTop: 8,
    "&:last-child": { borderBottom: "none" },
  },
  pendDetailLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: grayColor[1],
    marginBottom: 10,
  },
  pendDetailList: {
    margin: 0,
    padding: "0 0 0 18px",
    listStyle: "disc",
  },
  pendDetailItem: {
    fontSize: 13,
    color: grayColor[2],
    lineHeight: 1.45,
    marginBottom: 10,
    "&:last-child": { marginBottom: 0 },
  },
  pendTypePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 500,
    marginRight: 8,
    verticalAlign: "middle",
  },
  pendPillUpload: {
    background: "#ffebee",
    color: "#c62828",
  },
  pendPillReenvio: {
    background: "#fff8e1",
    color: "#f57f17",
  },
  pendPillDecision: {
    background: "#fff3e0",
    color: "#ef6c00",
  },
  pendPillError: {
    background: "#e3f2fd",
    color: "#1565c0",
  },
  pendItemTitle: {
    display: "inline",
    "& strong": { fontWeight: 600, color: grayColor[2] },
  },

  // ----- Timeline event (Linha do tempo pane) ----------------------------
  // Layout time / dot / text, parecido com tlItem do drawer do input
  // mas mais "list-like" (cada evento e uma linha simples). Reescrito
  // para manter consistencia visual com o cockpit acima.
  events: { padding: "4px 0" },
  event: {
    padding: "9px 18px",
    display: "grid",
    gridTemplateColumns: "76px 12px 1fr",
    columnGap: 10,
    alignItems: "flex-start",
    borderBottom: `1px solid ${grayColor[12]}`,
    "&:last-child": { borderBottom: "none" },
  },
  eventTime: {
    fontSize: 11,
    color: grayColor[1],
    paddingTop: 2,
    textAlign: "right",
    // Formato compacto "DD/mes HH:MM" (mai/2026) — caber em 1 linha
    // dentro dos 76px da coluna do grid ``classes.event``.
    whiteSpace: "nowrap",
  },
  eventDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginTop: 6,
    justifySelf: "center",
  },
  eventText: { fontSize: 12.5, color: grayColor[2], minWidth: 0 },
  eventWho: { color: grayColor[2], fontWeight: 500 },
  eventWhat: { color: grayColor[1] },
  eventFuture: {
    "& $eventTime": {
      fontSize: 10,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: grayColor[0],
    },
    "& $eventText": { color: grayColor[1] },
    "& $eventWho": { color: grayColor[1], fontWeight: 400 },
  },
  eventDotWaiting: {
    background: whiteColor,
    boxShadow: `inset 0 0 0 1.5px ${grayColor[6]}`,
  },
  nowMarker: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  nowTime: {
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 10,
  },

  // ----- Blocks list -----------------------------------------------------
  // Lista de blocos de calculo. Cada bloco e uma "row" clicavel que
  // expande para mostrar o log inline (sub-leva A: conteudo mock).
  blocksList: {
    display: "flex",
    flexDirection: "column",
  },
  blockItem: {
    borderBottom: (props = {}) =>
      `1px solid ${blendColors(props.clientColor || grayColor[5], whiteColor, 0.4)}`,
    "&:last-child": { borderBottom: "none" },
  },
  blockRow: {
    display: "grid",
    gridTemplateColumns: "1fr 150px 150px 24px",
    alignItems: "center",
    gap: 12,
    padding: "12px 18px",
    cursor: "pointer",
    transition: "background 120ms",
    background: whiteColor,
    "&:hover": { background: grayColor[13] },
  },
  blockRowOpen: {
    background: grayColor[13],
  },
  blockName: {
    fontSize: 13,
    color: grayColor[2],
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  blockNameMeta: {
    fontSize: 11,
    color: grayColor[1],
    marginTop: 2,
    fontWeight: 400,
  },
  blockStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 9px",
    borderRadius: 999,
    fontSize: 10.5,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    justifySelf: "start",
    "&::before": {
      content: '""',
      display: "inline-block",
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "currentColor",
    },
  },
  blockStatusCluster: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    justifySelf: "start",
  },
  blockStatusDone: {
    background: "rgba(76,175,80,0.12)",
    color: successColor[2],
  },
  // Paleta replicada do badge ``chipProcessing`` (badge do topo da
  // view, em ``inputs_components/styles.js``) — manter os dois em
  // paralelo: ambos representam o MESMO conceito ("modelagem em
  // processamento"), entao precisam compartilhar cor. ``successColor[0]``
  // saturado + sufixo ``"cc"`` (alpha ~80%) para o tom claro.
  // Efeito "onda" horizontal (gradiente 90deg de 200% largura
  // deslizando) — espelha o ``loading`` pip do StagePips em verde.
  // Se mexer aqui, ajustar la tambem.
  blockStatusRunning: {
    background: `linear-gradient(90deg, ${successColor[0]} 0%, ${successColor[0]}cc 50%, ${successColor[0]} 100%)`,
    backgroundSize: "200% 100%",
    animation: "$progressRunningWave 1400ms ease-in-out infinite",
    color: whiteColor,
  },
  blockStatusWaiting: {
    background: grayColor[12],
    color: grayColor[1],
  },
  blockStatusFailed: {
    background: "rgba(244,67,54,0.1)",
    color: dangerColor[2],
  },
  blockStatusStale: {
    background: "rgba(255,152,0,0.14)",
    color: warningColor[1],
  },
  blockDuration: {
    fontSize: 11,
    color: grayColor[1],
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    display: "grid",
    gap: 2,
    justifyItems: "end",
    "& strong": {
      fontSize: 11.5,
      color: grayColor[2],
      fontWeight: 500,
      whiteSpace: "nowrap",
    },
    "& small": {
      fontSize: 10.5,
      color: grayColor[1],
      whiteSpace: "nowrap",
    },
  },
  blockDurationPlaceholder: {
    minHeight: 1,
  },
  blockChevron: {
    color: grayColor[0],
    transition: "transform 200ms ease",
    fontSize: 18,
    justifySelf: "end",
  },
  blockChevronOpen: {
    transform: "rotate(180deg)",
  },
  // ``blockLogWrapper`` aplica a transicao de expand/collapse do log
  // sem desmontar o conteudo a cada toggle. ``max-height`` cresce ate
  // 2000px (cap generoso — logs longos cabem) e ``opacity`` evita o
  // "flash" de conteudo vindo do nada. Mesmo padrao usado no
  // ``InputBoxV3`` (collapsibleOpen / collapsibleClosed).
  blockLogWrapper: {
    overflow: "hidden",
    maxHeight: 0,
    opacity: 0,
    transition: "max-height 260ms ease, opacity 200ms ease, padding 220ms ease",
    background: whiteColor,
    borderTop: `1px solid transparent`,
  },
  blockLogWrapperOpen: {
    maxHeight: 2000,
    opacity: 1,
    borderTopColor: grayColor[12],
  },
  blockTabs: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "10px 18px 0",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[8]}`,
  },
  blockTab: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 11px",
    border: "1px solid transparent",
    borderBottom: "none",
    borderRadius: "5px 5px 0 0",
    fontSize: 11,
    color: grayColor[1],
    background: "transparent",
    fontFamily: "inherit",
    cursor: "pointer",
    "& .MuiSvgIcon-root": { fontSize: 15 },
    "&:hover": { color: grayColor[2] },
  },
  blockTabActive: {
    background: whiteColor,
    color: grayColor[2],
    borderColor: grayColor[8],
    marginBottom: -1,
    fontWeight: 500,
  },
  blockTabPanel: {
    padding: "14px 18px 18px",
    background: whiteColor,
  },
  blockTabPanelHead: {
    display: "flex",
    // Alinhar pela linha de BASE do texto da esquerda ("Log do
    // calculo" / "Dependencias" / "Detalhe tecnico") com a linha do
    // texto do botao a direita. ``baseline`` cola as duas linhas de
    // texto independentemente da altura visual do botao (que tem
    // padding/borda) — fica mais polido que ``center``, que media a
    // altura do bloco inteiro (botao + sombra).
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  blockLogPanelEmpty: {
    padding: "30px 18px",
    textAlign: "center",
    color: grayColor[1],
    fontSize: 12,
    fontStyle: "italic",
    background: grayColor[13],
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    "& .MuiSvgIcon-root": {
      fontSize: 22,
      color: grayColor[3],
    },
  },
  // ----- Dependencias por bloco (rodada 2026-04-28-bis) ---------------
  blockDepsPanel: {
    padding: "10px 18px 14px",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[12]}`,
  },
  blockDepsTitle: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: grayColor[1],
    fontWeight: 600,
    marginBottom: 0,
  },
  blockDepsMatrix: {
    border: `1px solid ${grayColor[8]}`,
    borderRadius: 6,
    overflow: "hidden",
    background: whiteColor,
  },
  blockDepsMatrixHead: {
    display: "grid",
    alignItems: "center",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[8]}`,
    fontSize: 10,
    color: grayColor[1],
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    "& > div": {
      padding: "8px 9px",
      borderRight: `1px solid ${grayColor[12]}`,
      minHeight: 36,
      textAlign: "center",
      "&:first-child": { textAlign: "left" },
      "&:last-child": { borderRight: "none" },
    },
  },
  blockDepsMatrixRow: {
    display: "grid",
    alignItems: "stretch",
    borderBottom: `1px solid ${grayColor[12]}`,
    fontSize: 12,
    "&:last-child": { borderBottom: "none" },
    "& > div": {
      padding: "8px 9px",
      borderRight: `1px solid ${grayColor[12]}`,
      minHeight: 38,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      "&:last-child": { borderRight: "none" },
    },
  },
  blockDepsMatrixName: {
    justifyContent: "flex-start !important",
    textAlign: "left !important",
    gap: 6,
    minWidth: 0,
    "& strong": {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: grayColor[2],
      fontWeight: 500,
    },
    "& small": {
      color: grayColor[1],
      fontSize: 11,
      marginLeft: 2,
      whiteSpace: "nowrap",
    },
  },
  blockDepTypeChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: 10,
    padding: 0,
    fontSize: 9.5,
    fontWeight: 600,
    background: grayColor[10],
    color: grayColor[2],
    lineHeight: 1,
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": { fontSize: 12, color: grayColor[2] },
  },
  blockDepsMatrixCell: {
    fontSize: 11,
    fontWeight: 500,
  },
  blockDepsMatrixCell_ready: {
    color: "#2e7d32",
    background: "#f1f8f2",
  },
  blockDepsMatrixCell_missing: {
    color: "#b71c1c",
    background: "#fff5f5",
  },
  blockDepsMatrixCell_stale: {
    color: "#bf6f00",
    background: "#fffaf1",
  },
  blockDepsMatrixCell_pending: {
    color: "#8a6d00",
    background: "#fffdf3",
  },
  blockDepsMatrixCell_outside_window: {
    color: grayColor[1],
    background: grayColor[13],
  },
  // Parse OK + cross-linking ainda em aberto. Visualmente proximo de
  // ``pending`` (mesmo amarelo discreto) — semantica e "aguardando
  // validacao cruzada", nao "input nao chegou".
  // Fonte menor + line-height compacto porque o rotulo de negocio
  // (``Avaliacao de integridade entre arquivos``) e' longo e quebra
  // em 2-3 linhas dentro da celula estreita da matriz.
  blockDepsMatrixCell_waiting_xlink: {
    color: "#8a6d00",
    background: "#fffdf3",
    fontSize: 9.5,
    lineHeight: 1.2,
    padding: "4px 6px !important",
    textAlign: "center",
  },
  // Parse OK + pergunta humana (input_parse/input_row_edit) em aberto.
  // Laranja canonico de ``pending_decision`` (mesma familia do badge
  // ``#ed6c02``) para diferenciar de ``waiting_xlink`` (amarelo) e
  // deixar claro que ha uma decisao a responder, nao apenas espera.
  blockDepsMatrixCell_waiting_decision: {
    color: "#b25a00",
    background: "#fff4e8",
    fontSize: 9.5,
    lineHeight: 1.2,
    padding: "4px 6px !important",
    textAlign: "center",
  },
  blockDepsMatrixCellEmpty: {
    color: grayColor[3],
    background: grayColor[13],
    fontSize: 11,
  },
  blockDepsList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  blockDepRow: {
    display: "grid",
    gridTemplateColumns: "20px 1fr auto",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 4,
    "&:hover": { background: whiteColor },
  },
  blockDepRowDrifted: {
    background: blendColors("#fff3e0", whiteColor, 0.55),
    "&:hover": { background: blendColors("#fff3e0", whiteColor, 0.4) },
  },
  blockDepName: {
    color: grayColor[0],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "& small": {
      color: grayColor[1],
      fontSize: 11,
      marginLeft: 6,
    },
  },
  blockDepBadge: {
    fontSize: 10,
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: grayColor[0],
    background: grayColor[12],
  },
  blockDepBadgeReady: {
    color: "#2e7d32",
    background: "#e8f5e9",
  },
  blockDepBadgeMissing: {
    color: "#b71c1c",
    background: "#ffebee",
  },
  blockDepBadgePending: {
    color: "#8a6d00",
    background: "#fff8e1",
  },
  blockDepDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: grayColor[3],
  },
  blockDepDotReady: { background: "#2e7d32" },
  blockDepDotMissing: { background: "#b71c1c" },
  blockDepDotPending: { background: "#f0a800" },
  blockDepDriftMark: {
    fontSize: 10,
    color: "#bf6f00",
    fontWeight: 600,
    marginLeft: 6,
  },
  blockRecalcBar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0 18px 12px",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[12]}`,
  },
  blockRecalcButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    // Reduzido de 26 -> 20px e fontSize 12 -> 11 para alinhar
    // visualmente com a altura do titulo da aba ("Log do calculo")
    // que vive a esquerda. Mesmo case-style (uppercase + letter
    // spacing) ajuda a fazer os dois textos parecerem "pertencer
    // ao mesmo nivel hierarquico" da UI.
    minHeight: 20,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    padding: "1px 9px",
    border: "none",
    borderRadius: 3,
    background: (props = {}) => props.clientColor || dangerColor[2],
    color: theme.palette.custom?.clientTextColor || whiteColor,
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow:
      "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
    transition: "box-shadow 120ms ease, opacity 120ms ease",
    "& .MuiSvgIcon-root": { fontSize: 13 },
    "&:hover:not(:disabled)": {
      boxShadow:
        "0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12), 0 2px 4px -1px rgba(0,0,0,.2)",
    },
    "&:disabled": {
      cursor: "default",
      opacity: 0.5,
    },
  },
  blockRecalcTooltipWrap: {
    display: "inline-flex",
  },
  blockRecalcIconButton: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "none",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: grayColor[1],
    cursor: "pointer",
    transition: "background 120ms ease, color 120ms ease, opacity 120ms ease",
    "& .MuiSvgIcon-root": { fontSize: 16 },
    "&:hover:not(:disabled)": {
      background: grayColor[12],
      color: (props = {}) => props.clientColor || grayColor[0],
    },
    "&:disabled": {
      cursor: "default",
      opacity: 0.45,
    },
  },
  blockRecalcSpinner: {
    width: 11,
    height: 11,
    borderRadius: "50%",
    border: `2px solid ${grayColor[8]}`,
    borderTopColor: (props = {}) => props.clientColor || dangerColor[2],
    animation: "$tlSpin 800ms linear infinite",
    flexShrink: 0,
  },
  blockErrorPanel: {
    padding: 10,
    background: "#fff5f5",
    border: `1px solid #ffcdd2`,
    borderRadius: 5,
    fontSize: 11,
    color: "#9b2c2c",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  // Hint discreto sobre o estado mock dessa pane (sub-leva A).
  blockMockHint: {
    padding: "10px 18px",
    background: blendColors("#fff8e1", whiteColor, 0.6),
    borderBottom: `1px solid ${grayColor[12]}`,
    fontSize: 11,
    color: "#8a6d00",
    display: "flex",
    alignItems: "center",
    gap: 8,
    "& strong": { fontWeight: 600 },
  },

  // ----- Empty state padronizado das panes -------------------------------
  paneEmpty: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "60px 32px",
    color: grayColor[1],
    fontSize: 13,
    textAlign: "center",
    "& .MuiSvgIcon-root": {
      fontSize: 44,
      color: grayColor[3],
    },
  },
  paneEmptyTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: grayColor[1],
  },
  paneEmptySub: {
    fontSize: 12,
    color: grayColor[0],
    maxWidth: 340,
    lineHeight: 1.5,
  },

  // ----- Pane head shared ------------------------------------------------
  // Padronizado com ``sectionTitle`` do drawer dos inputs (NowPane,
  // secao "Este mes"): fontSize 11, uppercase, letterSpacing 0.1em,
  // color grayColor[0]. Manter alinhado para que os 2 drawers tenham
  // a mesma "linguagem visual" de cabecalho de secao.
  paneHead: {
    padding: "14px 18px 12px",
    borderBottom: `1px solid ${grayColor[12]}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  // Titulo isolado para ``DrawerPaneLoadingLayout``: mesma geometria que
  // ``paneHead``, sem divisória sob o texto.
  paneHeadLoading: {
    padding: "14px 18px 8px",
    borderBottom: "none",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  paneHeadTitle: {
    margin: 0,
    fontSize: 11,
    fontWeight: 500,
    color: grayColor[0],
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  paneHeadCount: {
    marginLeft: "auto",
    fontSize: 11,
    color: grayColor[1],
    textTransform: "none",
    letterSpacing: 0,
    fontWeight: 400,
  },

  // ----- Loader padrao (espelha ``paneLoading*`` do drawer de inputs) ----
  paneLoading: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    padding: "48px 24px",
    color: grayColor[1],
  },
  paneLoadingGridWrap: {
    width: 32,
    height: 32,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paneLoadingLabel: {
    fontSize: 12,
    color: grayColor[1],
    letterSpacing: "0.02em",
  },
  paneLoadingLayoutSlot: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    padding: "10px 24px 92px",
    color: grayColor[1],
  },
  "@keyframes tlSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  // Shimmer/onda do ``blockStatusRunning``: gradient de 200% deslizando
  // lateralmente. Espelha ``pipsLoadingShimmer`` do StagePips.
  "@keyframes progressRunningWave": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
}));

export default useProgressStyles;
